import { atom } from "jotai"
import { focusAtom } from "jotai-optics"
import { store } from "../store"
import { currentAdjustmentsAtom } from "../store-history"
import {
	ColorMatchImageRecord,
	deleteColorMatchImage,
	storeColorMatchImage,
	storeColorMatchImages,
} from "@/storage/indexeddb"
import { resizeImageToSize } from "@/core/utils/resize-image"
import { uuid } from "@/utils/uuid"
import {
	ColorMatchImageItem,
	ColorMatchImageService,
	ImageResponseDTO,
} from "./color-match-service"
import { atomWithQuery } from "jotai-tanstack-query"
import { userQueryAtom } from "@/api/users-api"
import { Sentry } from "@/telemetry/sentry"
import toast from "react-hot-toast"
import { isHEICFile } from "@/utils/file"
import { convertHEICToImageData } from "@/utils/heic-converter"
import { fetchWithNetworkBehavior } from "@/api/fetch-with-network-behavior"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"

export const fetchColorMatchImagesAtom = atomWithQuery<ColorMatchImageItem[]>((get) => {
	const userId = get(userQueryAtom).data?.userId

	return {
		queryKey: [ColorMatchImageService.queryKey, userId],
		queryFn: ColorMatchImageService.fetchImages,
		enabled: Boolean(userId),
	}
})

export const colorMatchImagesAtom = atom<{
	data: ColorMatchImageItem[]
	isInitialLoading: boolean
}>((get) => {
	const { data, isInitialLoading, isError } = get(fetchColorMatchImagesAtom)
	return data && !isError
		? {
				data,
				isInitialLoading,
			}
		: {
				data: [],
				isInitialLoading,
			}
})

export const currentColorMatchIndexAtom = atom(0)

export const colorMatchImagesMapAtom = atom((get) => {
	const images = get(colorMatchImagesAtom).data
	return Object.fromEntries(images.map((image) => [image.id, image]))
})

export const currentColorMatchImageIdAtom = atom((get) => {
	const adjustments = get(currentAdjustmentsAtom)
	return adjustments.colorMatchImageId
})

export const colorMatchImageUrlsAtom = atom<{ [imageId: string]: string }>({})
export const focusedColorMatchImageURLAtom = (imageId: string) =>
	focusAtom(colorMatchImageUrlsAtom, (optic) => optic.prop(imageId))

const checkImageLimit = (adding: number) => {
	const images = store.get(colorMatchImagesAtom).data

	if (images.length + adding > 50) {
		toast.error(i18n.t("components:colorMatchState.youveReachedThe50Source"), {
			duration: 5000,
		})
		return false
	}
	return true
}
export const importColorMatchImages = async (files: File[]) => {
	if (!checkImageLimit(files.length)) {
		return
	}

	toast.loading("Importing Color Match source images...", { id: "batchImport" })
	try {
		const userId = store.get(userQueryAtom).data?.userId
		if (!userId) {
			throw new Error("Current user is unavailable")
		}
		const records: ColorMatchImageRecord[] = []
		for (const file of files) {
			const inputBuffer = await file.arrayBuffer()
			let inputBlob: Blob | ImageData
			if (isHEICFile(file)) {
				inputBlob = await convertHEICToImageData(inputBuffer)
			} else {
				inputBlob = new Blob([inputBuffer], { type: file.type })
			}

			const resizeBlob = await resizeImageToSize({
				image: inputBlob,
				options: { outputQuality: 0.9 },
				longestSide: 1600,
			})

			const imageRecord: ColorMatchImageRecord = {
				id: uuid(),
				name: file.name,
				data: resizeBlob,
				createdAt: new Date(),
				updatedAt: new Date(),
				userId,
			}
			records.push(imageRecord)
		}

		await ColorMatchImageService.uploadColorMatchImages(records)
		await storeColorMatchImages(records)
		store.set(currentColorMatchIndexAtom, 0)
	} catch (e) {
		console.error("Importing images", e)
		Sentry.captureException(e)
		if (e instanceof Error) {
			toast.error(
				i18n.t("store:colorMatchState.colorMatchImportFailed", { detail: e.message }),
			)
		}
	} finally {
		toast.dismiss("batchImport")
	}
}

export const importColorMatchImageFromSample = async (sample: ImageResponseDTO) => {
	if (!checkImageLimit(1)) {
		return
	}
	try {
		const userId = store.get(userQueryAtom).data?.userId
		if (!userId) {
			throw new Error("Current user is unavailable")
		}
		const data = await fetchWithNetworkBehavior(sample.url, undefined, {
			networkIntent: "active",
		}).then((res) => res.blob())
		const resizeBlob = await resizeImageToSize({
			image: data,
			options: { outputQuality: 0.9 },
			longestSide: 1600,
		})

		const imageRecord: ColorMatchImageRecord = {
			id: uuid(),
			name: sample.displayName,
			data: resizeBlob,
			createdAt: new Date(),
			updatedAt: new Date(),
			contentDigest: sample.contentHash,
			userId,
		}

		await ColorMatchImageService.createColorMatchImage(imageRecord, sample.url)
		await storeColorMatchImage(imageRecord)
		store.set(currentColorMatchIndexAtom, 0)
	} catch (e) {
		console.error("Importing image from sample", e)
		Sentry.captureException(e)
		if (e instanceof Error) {
			toast.error(
				i18n.t("store:colorMatchState.colorMatchImportFailed", { detail: e.message }),
			)
		}
	}
}

export const retryColorMatchImageUpload = async (image: ColorMatchImageItem) => {
	if (!image.data) {
		return
	}
	const userId = store.get(userQueryAtom).data?.userId
	if (!userId || image.userId !== userId) {
		return
	}

	await ColorMatchImageService.uploadColorMatchImages([
		{
			id: image.id,
			name: image.name,
			data: image.data,
			createdAt: image.createdAt,
			updatedAt: image.updatedAt,
			contentDigest: image.contentDigest,
			userId,
		},
	])
}

export const removeColorMatchImage = async (imageId: string) => {
	try {
		await deleteColorMatchImage(imageId)
		await ColorMatchImageService.deleteImage(imageId)

		const imageUrls = store.get(colorMatchImageUrlsAtom)
		const url = imageUrls[imageId]

		if (url) {
			URL.revokeObjectURL(url)
			delete imageUrls[imageId]
		}
		store.set(colorMatchImageUrlsAtom, { ...imageUrls })
	} catch (e) {
		console.error("Deleting image", e)
		Sentry.captureException(e)
	}
}
